/**
 * GiOrbitLogo — the trademarked GI orbital mark.
 *
 * Pure SVG. A dashed outer circle, a tilted equator ellipse, a solid
 * core dot, and two letters "G" (top) + "I" (bottom) that orbit the
 * centre on a 7-second loop. Each letter sits inside a counter-
 * rotating group so the glyph stays visually upright regardless of
 * the outer rotation angle — the same trick the ATLAS reference
 * HTML uses.
 *
 * Three roles in the product:
 *
 *   - brand mark   (size="sm", used in the app header next to "Engine")
 *   - loading spinner (size="md", used wherever a Spinner used to live)
 *   - hero mark    (size="lg", for big wait screens — not yet used)
 *
 * All strokes/fills use `currentColor` so the logo tracks the
 * surrounding text colour and inverts cleanly for dark mode.
 *
 * Accessibility: when used as a spinner we expose `role="status"` and
 * an aria-label so screen readers announce the loading state. For the
 * static brand usage the image is decorative (aria-hidden).
 */

import { cn } from '@/utils/cn';

export type GiOrbitSize = 'xs' | 'sm' | 'md' | 'lg';

const SIZES: Record<GiOrbitSize, { px: number; fontPx: number; orbitR: number; dotR: number }> = {
  xs: { px: 20, fontPx: 7,  orbitR: 8,  dotR: 1.8 },
  sm: { px: 32, fontPx: 10, orbitR: 13, dotR: 2.8 },
  md: { px: 56, fontPx: 14, orbitR: 22, dotR: 4.2 },
  lg: { px: 96, fontPx: 22, orbitR: 36, dotR: 7.0 },
};

export function GiOrbitLogo({
  size = 'sm',
  className,
  label,
  decorative = false,
}: {
  size?: GiOrbitSize;
  className?: string;
  /** Screen-reader label. Defaults to "GI Engine" (brand) or
   *  "Loading" (when size is md/lg). Ignored when `decorative`. */
  label?: string;
  /** Marks the mark as presentational — used in places where a
   *  sibling label already announces the context (header wordmark,
   *  dense spinner stacks). */
  decorative?: boolean;
}) {
  const s = SIZES[size];
  const viewBoxHalf = s.px / 2;
  // sm = brand mark. Everything else is a wait affordance.
  const isSpinner = size !== 'sm';
  const ariaLabel = decorative ? undefined : (label ?? (isSpinner ? 'Loading' : 'GI Engine'));

  return (
    <svg
      width={s.px}
      height={s.px}
      viewBox={`${-viewBoxHalf} ${-viewBoxHalf} ${s.px} ${s.px}`}
      className={cn('shrink-0 text-ink', className)}
      role={decorative ? 'presentation' : isSpinner ? 'status' : 'img'}
      aria-label={ariaLabel}
      aria-hidden={decorative || undefined}
      overflow="visible"
    >
      {/* Dashed outer circle */}
      <circle
        r={s.orbitR}
        stroke="currentColor"
        strokeWidth={0.6}
        strokeDasharray="1.5 2.5"
        fill="none"
        opacity={0.22}
      />

      {/* Tilted equator ellipse */}
      <ellipse
        rx={s.orbitR}
        ry={s.orbitR * 0.36}
        stroke="currentColor"
        strokeWidth={0.7}
        fill="none"
        opacity={0.28}
        transform="rotate(-25)"
      />

      {/* Core dot */}
      <circle r={s.dotR} fill="currentColor" opacity={0.9} />

      {/* Spinning group — two orbiting letters + two side dots. The
       *  inner letters each counter-rotate via `gi-orbit-unspin` so
       *  they stay upright while their translate parent rotates. */}
      <g
        style={{
          transformOrigin: '0px 0px',
          animation: 'gi-orbit-spin 7s linear infinite',
        }}
      >
        <g transform={`translate(0, ${-s.orbitR})`}>
          <g
            style={{
              transformOrigin: '0px 0px',
              animation: 'gi-orbit-unspin 7s linear infinite',
            }}
          >
            <text
              x="0"
              y="0"
              textAnchor="middle"
              dominantBaseline="central"
              fill="currentColor"
              opacity={0.9}
              style={{
                fontFamily: '"DM Mono", ui-monospace, monospace',
                fontSize: s.fontPx,
                fontWeight: 400,
              }}
            >
              G
            </text>
          </g>
        </g>
        <g transform={`translate(0, ${s.orbitR})`}>
          <g
            style={{
              transformOrigin: '0px 0px',
              animation: 'gi-orbit-unspin 7s linear infinite',
            }}
          >
            <text
              x="0"
              y="0"
              textAnchor="middle"
              dominantBaseline="central"
              fill="currentColor"
              opacity={0.9}
              style={{
                fontFamily: '"DM Mono", ui-monospace, monospace',
                fontSize: s.fontPx,
                fontWeight: 400,
              }}
            >
              I
            </text>
          </g>
        </g>
        <circle cx={s.orbitR} cy={0} r={1.3} fill="currentColor" opacity={0.2} />
        <circle cx={-s.orbitR} cy={0} r={1.3} fill="currentColor" opacity={0.2} />
      </g>
    </svg>
  );
}
